import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';

import { get as getUser } from 'js/lib/user';

const DEFAULT_LANG = 'en-US';

export enum SpeechRecognitionStatus {
  Idle = 'idle',
  Listening = 'listening',
  SpeechStarted = 'speechStarted',
  SoundEnded = 'soundEnded',
  StoppedListening = 'stoppedListening',
  Error = 'error',
}

// The Web Speech API constrains `SpeechRecognitionErrorEvent.error` to this string enum,
// but the DOM typings in this codebase do not expose that narrow union.
export const speechRecognitionErrorCodes = [
  'no-speech',
  'aborted',
  'audio-capture',
  'network',
  'not-allowed',
  'service-not-allowed',
  'language-not-supported',
  'phrases-not-supported',
  'bad-grammar',
] as const;

export type SpeechRecognitionErrorCode = (typeof speechRecognitionErrorCodes)[number];
export const browserNotSupportedError = 'Your browser does not support the Web Speech API.' as const;
export type SpeechToTextError = SpeechRecognitionErrorCode | typeof browserNotSupportedError;

export const addPunctuation = (text: string): string => {
  let punctuatedText = text.trim();
  if (!punctuatedText) return '';

  const questionWords = [
    'who',
    'what',
    'where',
    'when',
    'why',
    'how',
    'can',
    'would',
    'could',
    'should',
    'is',
    'are',
    'will',
    'does',
    'did',
  ];
  const conjunctionWords = ['and', 'but', 'or', 'so', 'nor', 'yet'];
  const lowerCasedText = punctuatedText.toLowerCase();

  if (questionWords.some((word) => lowerCasedText.startsWith(word))) {
    punctuatedText = punctuatedText.charAt(0).toUpperCase() + punctuatedText.slice(1) + '?';
  } else if (!punctuatedText.endsWith('.') && !punctuatedText.endsWith('?') && !punctuatedText.endsWith('!')) {
    punctuatedText = punctuatedText.charAt(0).toUpperCase() + punctuatedText.slice(1) + '.';
  }

  for (let i = 0; i < conjunctionWords.length; i += 1) {
    const conjunction = conjunctionWords[i];
    if (punctuatedText.includes(` ${conjunction} `)) {
      punctuatedText = punctuatedText.replaceAll(` ${conjunction}`, `, ${conjunction}`);
    }
  }

  return punctuatedText;
};

/**
 * Thin wrapper around the Web Speech API for speech recognition with
 * customisations like automatic punctuation and auto-stop after silence.
 *
 * Note: currently only English and Spanish have been tested to work reliably.
 *
 * @remarks
 * Avoid changing `automaticPunctuation` (or any other prop that is a dep of the
 * recognition event-setup effect) while the user is actively speaking. Doing so
 * causes the effect to re-run: the cleanup calls `recognition.abort()`, which
 * suppresses `onend`, meaning `onStopListening` will not fire and any caller
 * tracking listening state (e.g. via a ref) will be left stale.
 */
const useSpeechToText = (props: {
  stopAfterSeconds?: number;
  automaticPunctuation?: boolean;
  language?: string;
  supportedLanguages?: string[];
  onStopListening?: () => void;
  onStartListening?: () => void;
  onError?: (error: SpeechToTextError) => void;
  onSupportStatus?: (isSupported: boolean) => void;
  onTranscript?: (transcript: string, isInterim: boolean) => void;
}) => {
  const {
    stopAfterSeconds,
    automaticPunctuation,
    language,
    supportedLanguages,
    onStartListening,
    onStopListening,
    onError,
    onSupportStatus,
    onTranscript,
  } = props;

  const [transcriptFinal, setTranscriptFinal] = useState<string>('');
  const [transcriptInterim, setTranscriptInterim] = useState<string>('');
  const [listening, setListening] = useState<boolean>(false);
  const [status, setStatus] = useState<SpeechRecognitionStatus>(SpeechRecognitionStatus.Idle);
  const [error, setError] = useState<SpeechToTextError | null>(null);
  // Lazy initialiser: detect support synchronously so the mic button never
  // flashes on unsupported browsers between the first paint and the effect.
  const [isSupportedBrowser] = useState<boolean>(() =>
    Boolean(globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition)
  );
  const [recognition, setRecognition] = useState<null | SpeechRecognition>(null);

  // Refs so startListening / stopListening always call the latest callbacks
  // without needing to list them as useCallback deps (which would recreate
  // stopListening → recreate stopListeningDebounce on every render).
  const onStartListeningRef = useRef(onStartListening);
  onStartListeningRef.current = onStartListening;
  const onStopListeningRef = useRef(onStopListening);
  onStopListeningRef.current = onStopListening;
  const onSupportStatusRef = useRef(onSupportStatus);
  onSupportStatusRef.current = onSupportStatus;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const userLanguage = language ?? getUser().locale ?? DEFAULT_LANG;

  // Default: support all languages the Web Speech API supports.
  const isSupportedLanguage = supportedLanguages ? supportedLanguages.includes(userLanguage) : true;
  const recognitionLanguage = isSupportedLanguage ? userLanguage : DEFAULT_LANG;

  useEffect(() => {
    const SpeechRecognition = globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = recognitionLanguage;
      setRecognition(recognitionInstance);
      return () => {
        // Abort any active session before the instance is replaced (e.g., on language switch).
        recognitionInstance.abort();
      };
    } else {
      // isSupportedBrowser is already false from the lazy initialiser;
      // only set the error and notify the caller.
      setError(browserNotSupportedError);
      onSupportStatusRef.current?.(false);
    }
  }, [recognitionLanguage]);

  const startListening = useCallback<() => void>(() => {
    if (recognition) {
      recognition.start();
      onStartListeningRef.current?.();
    }
  }, [recognition]);

  const stopListening = useCallback<() => void>(() => {
    if (recognition) {
      recognition.stop();
      // Do NOT call onStopListeningRef here — recognition.onend fires for every
      // termination path and is the single authoritative stop signal.
    }
  }, [recognition]);

  const stopListeningDebounce = useMemo(() => {
    if (stopAfterSeconds == null) {
      return undefined;
    }
    return debounce(stopListening, stopAfterSeconds * 1_000);
  }, [stopListening, stopAfterSeconds]);

  useEffect(() => {
    return () => stopListeningDebounce?.cancel();
  }, [stopListeningDebounce]);

  useEffect(() => {
    if (recognition) {
      recognition.onstart = () => {
        setStatus(SpeechRecognitionStatus.Listening);
        setListening(true);
      };

      recognition.onspeechstart = () => {
        setStatus(SpeechRecognitionStatus.SpeechStarted);
        setListening(true);
      };

      recognition.onsoundend = () => {
        setStatus(SpeechRecognitionStatus.SoundEnded);
        setListening(false);
      };

      recognition.onend = () => {
        setStatus(SpeechRecognitionStatus.StoppedListening);
        setListening(false);
        // Call via ref so OS/browser-initiated terminations are reported to the
        // caller without adding onStopListening to this effect's dep array.
        onStopListeningRef.current?.();
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let interimTranscript = '';
        let finalTranscript = '';
        let transcriptResult = '';

        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          if (!result) continue;
          transcriptResult = result[0]?.transcript ?? '';

          if (result.isFinal) {
            finalTranscript += automaticPunctuation ? addPunctuation(transcriptResult) : transcriptResult;
          } else {
            interimTranscript += transcriptResult;
          }
        }

        if (finalTranscript) {
          onTranscriptRef.current?.(finalTranscript + ' ', false);
          setTranscriptFinal(finalTranscript);
          setTranscriptInterim('');
        } else {
          onTranscriptRef.current?.(interimTranscript, true);
          setTranscriptInterim(interimTranscript);
        }

        stopListeningDebounce?.();
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        const recognitionError = event.error as SpeechRecognitionErrorCode;
        setError(recognitionError);
        setStatus(SpeechRecognitionStatus.Error);
        onErrorRef.current?.(recognitionError);
      };
    }

    return () => {
      if (recognition) {
        // abort() silences the onend event; stop() would fire onend and trigger
        // a spurious onStopListening callback on every language-switch cleanup.
        recognition.abort();
      }
    };
  }, [automaticPunctuation, recognition, stopListeningDebounce]);

  return {
    transcriptFinal,
    transcriptInterim,
    listening,
    status,
    error,
    startListening,
    stopListening,
    clearTranscript: () => setTranscriptFinal(''),
    isSupportedBrowser,
    isSupportedLanguage,
    recognition,
  };
};

export default useSpeechToText;
